<template>
  <div class="duplicate-project-wizard" :class="{ 'duplicate-project-wizard--compact': !showHeader }">
    <div
      ref="wizardHeader"
      class="wizard-sticky-top"
      :class="{ 'wizard-sticky-top--compact': !showHeader }"
    >
      <div class="wizard-header" :class="{ 'wizard-header--hidden': !showHeader }">
        <div class="duplication-icon">
          <img src="@/csmworkflow/img/duplication.svg" />
        </div>
         <div class="wizard-header-content">
          <h4>WIZARD</h4>
          <h2>Duplicate Project Wizard</h2>
          <p class="mb-0" v-if="sourceProject">
            {{ sourceProject.title }} ({{ sourceProject.project_number }})
          </p>
         </div>
      </div>

      <div class="wizard-sticky-main">
        <WizardProgressBar
          :steps="steps"
          :currentStep="currentStep"
          :current-step-status="currentStep === 3 ? duplicationStatus : ''"
          :max-clickable-step="stepNavigationDisabled ? 0 : maxVisitedStep"
          :navigation-disabled="stepNavigationDisabled"
          @step-click="goToStep"
        />

        <div class="wizard-action-bar">
          <b-spinner v-if="duplicationInProgress" label="Loading..." variant="light"></b-spinner>
          <div
            v-else-if="duplicationStatus === 'failed'"
            class="wizard-action-status wizard-action-status--failed"
          >
            <div class="wizard-action-status__indicator">
              <i class="fas fa-times"></i>
            </div>
            <span class="wizard-action-status__label">Error</span>
          </div>
          <div
            v-else-if="duplicationStatus === 'completed'"
            class="wizard-action-status wizard-action-status--completed"
          >
            <div class="wizard-action-status__indicator">
              <i class="fas fa-check"></i>
            </div>
            <span class="wizard-action-status__label">Finished</span>
          </div>
          <button
            v-if="!stepNavigationDisabled"
            class="btn btn-secondary wizard-btn-cancel"
            @click="cancel"
          >Cancel</button>
          <div v-if="!stepNavigationDisabled" class="wizard-action-bar__right">
            <button
              v-if="currentStep > 1"
              class="btn btn-primary wizard-btn-prev"
              @click="previousStep"
            >Previous</button>
            <button
              v-if="currentStep < 3"
              class="btn btn-primary wizard-btn-next"
              :disabled="!canProceed"
              @click="nextStep"
            >Next</button>
          </div>
        </div>
      </div>

    </div>

    <div class="wizard-header-spacer" :style="{ height: expandedWizardHeaderHeight + 'px' }"></div>

    <div class="wizard-body" :style="{ transform: wizardBodyTransform }">
      <div v-if="loading" class="text-center p-5">
        <b-spinner label="Loading..."></b-spinner>
        <p class="mt-2">Loading project details...</p>
      </div>

      <div v-else-if="error" class="text-center p-5">
        <p class="text-danger">{{ error }}</p>
        <button class="btn btn-primary" @click="$router.go(-1)">Go Back</button>
      </div>

      <div v-else>
        <!-- Step 1: Project Details -->
        <DuplicateStep1ProjectDetails
          v-if="currentStep === 1"
          ref="step1"
          :allProjects="allProjects"
        />

        <!-- Step 2: Select Deliverables -->
        <DuplicateStep2SelectDeliverables
          v-if="currentStep === 2"
          ref="step2"
        />

        <!-- Step 3: Finish (placeholder) -->
        <DuplicateStep3Finish
          v-if="currentStep === 3"
          ref="step3"
          @status-change="duplicationStatus = $event"
        />
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import WizardProgressBar from "@/csmworkflow/js/components/common/WizardProgressBar.vue";
import DuplicateStep1ProjectDetails from "@/csmworkflow/js/components/duplication/DuplicateStep1ProjectDetails.vue";
import DuplicateStep2SelectDeliverables from "@/csmworkflow/js/components/duplication/DuplicateStep2SelectDeliverables.vue";
import DuplicateStep3Finish from "@/csmworkflow/js/components/duplication/DuplicateStep3Finish.vue";

export default {
  name: "DuplicateProjectWizard",
  components: {
    WizardProgressBar,
    DuplicateStep1ProjectDetails,
    DuplicateStep2SelectDeliverables,
    DuplicateStep3Finish,
  },
  data() {
    return {
      steps: ["Enter New Project Details", "Select Deliverables to Duplicate", "Finalize"],
      loading: true,
      error: null,
      allProjects: [],
      confirmedWizardLeave: false,
      leaveWizardMessage: "Are you sure you want to stop duplicating this project?",
      showHeader: true,
      lastScrollY: 0,
      resizeObserver: null,
      compactWizardHeaderHeight: 0,
      expandedWizardHeaderHeight: 0,
      maxVisitedStep: 1,
      duplicationStatus: "",
    };
  },
  computed: {
    ...mapState({
      duplicationWizard: state => state.duplicationWizard,
    }),
    currentStep() {
      return this.duplicationWizard.step;
    },
    sourceProject() {
      return this.duplicationWizard.sourceProject;
    },
    duplicationStarted() {
      return this.duplicationWizard.duplicationStarted;
    },
    stepNavigationDisabled() {
      return this.currentStep === 3 && this.duplicationStarted;
    },
    duplicationInProgress() {
      return this.stepNavigationDisabled && !["completed", "failed"].includes(this.duplicationStatus);
    },
    shouldWarnOnLeave() {
      return !["completed", "failed"].includes(this.duplicationStatus);
    },
    canProceed() {
      if (this.currentStep === 1) {
        const title = this.duplicationWizard.projectOptions.title;
        return title && title.trim().length > 0;
      }
      return true;
    },
    wizardBodyTransform() {
      if (this.showHeader || !this.compactWizardHeaderHeight) {
        return "translateY(0)";
      }

      const offset = Math.max(0, this.expandedWizardHeaderHeight - this.compactWizardHeaderHeight);
      return `translateY(-${offset}px)`;
    },
  },
  watch: {
    showHeader() {
      this.$nextTick(this.updateWizardHeaderHeight);
    },
  },
  async created() {
    const projectId = this.$route.params.projectId;
    if (!projectId) {
      this.error = "No project ID provided.";
      this.loading = false;
      return;
    }
    try {
      const ensureSettings = this.$store.dispatch("fetchSystemSettings");
      const ensureUsers = this.$store.dispatch("fetchUsers");
      const ensureActiveUsers = this.$store.dispatch("fetchActiveUsers");
      const result = await this.$store.dispatch("initDuplicationWizard", projectId);
      await Promise.all([ensureSettings, ensureUsers, ensureActiveUsers]);
      this.allProjects = result.allProjects || [];
      this.loading = false;
    } catch (e) {
      this.error = "Failed to load project details.";
      this.loading = false;
    }
  },
  mounted() {
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
    window.addEventListener("resize", this.updateWizardHeaderHeight);
    document.addEventListener("scroll", this.handleScroll, true);
    this.lastScrollY = this.getScrollTop(document);
    this.observeWizardHeader();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.shouldWarnOnLeave || this.confirmedWizardLeave || window.confirm(this.leaveWizardMessage)) {
      this.confirmedWizardLeave = true;
      next();
      return;
    }

    next(false);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    window.removeEventListener("resize", this.updateWizardHeaderHeight);
    document.removeEventListener("scroll", this.handleScroll, true);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.$store.commit("RESET_DUPLICATION_WIZARD");
  },
  methods: {
    observeWizardHeader() {
      this.$nextTick(() => {
        if (!this.$refs.wizardHeader) return;

        this.updateWizardHeaderHeight();
        if (typeof ResizeObserver === "undefined") return;

        this.resizeObserver = new ResizeObserver(this.updateWizardHeaderHeight);
        this.resizeObserver.observe(this.$refs.wizardHeader);
      });
    },
    updateWizardHeaderHeight() {
      if (!this.$refs.wizardHeader) return;
      const height = this.$refs.wizardHeader.offsetHeight;
      if (this.showHeader) {
        this.expandedWizardHeaderHeight = height;
        return;
      }

      this.compactWizardHeaderHeight = height;
    },
    getScrollTop(target) {
      if (target === document || target === document.documentElement || target === document.body) {
        return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      }

      return typeof target.scrollTop === "number" ? target.scrollTop : 0;
    },
    isModalScrollTarget(target) {
      return target instanceof Element && Boolean(target.closest(".modal, .modal-backdrop"));
    },
    handleScroll(event) {
      if (document.body.classList.contains("modal-open") || this.isModalScrollTarget(event.target)) {
        return;
      }

      const scrollY = this.getScrollTop(event.target);
      const diff = scrollY - this.lastScrollY;

      if (Math.abs(diff) < 8) {
        return;
      }

      if (scrollY < 50) {
        this.showHeader = true;
      } else {
        this.showHeader = diff < 0;
      }

      this.lastScrollY = scrollY;
    },
    nextStep() {
      if (this.currentStep === 1 && this.$refs.step1) {
        if (!this.$refs.step1.isValid()) return;
      }
      if (this.currentStep < 3) {
        this.setWizardStep(this.currentStep + 1);
      }
    },
    previousStep() {
      if (this.currentStep > 1 && !(this.currentStep === 3 && this.duplicationStarted)) {
        this.setWizardStep(this.currentStep - 1);
      }
    },
    goToStep(step) {
      if (step === this.currentStep || step > this.maxVisitedStep) {
        return;
      }
      if (this.currentStep === 3 && this.duplicationStarted) return;
      if (step > this.currentStep && this.currentStep === 1 && this.$refs.step1) {
        if (!this.$refs.step1.isValid()) return;
      }
      this.setWizardStep(step);
    },
    setWizardStep(step) {
      this.maxVisitedStep = Math.max(this.maxVisitedStep, step);
      this.$store.commit("SET_DUPLICATION_STEP", step);
    },
    beforeUnloadHandler(e) {
      if (!this.shouldWarnOnLeave) {
        return;
      }
      e.preventDefault();
      e.returnValue = this.leaveWizardMessage;
      return this.leaveWizardMessage;
    },
    cancel() {
      if (!window.confirm(this.leaveWizardMessage)) return;
      this.confirmedWizardLeave = true;
      this.$router.go(-1);
    },
  },
};
</script>
